import { CheckCircle, Circle, ErrorOutline, ExpandLess, ExpandMore } from '@mui/icons-material';
import {
  Box,
  Button,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Link,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import { ProductDto, S4DProductDto } from '@kotipizzagroup/kotipizza-products-api-client';
import styled from 'styled-components';
import {
  getOrphanS4DProducts,
  isBlockingS4DIssueKind,
  S4DIssueKind,
  S4DProductComparison,
  S4DProductStatus,
  S4DToppingRef,
} from '../utils/s4dProductComparison';
import { getS4DIssues, getS4DIssueText, isBlockingS4DIssue, S4DIssue, S4DPage } from '../utils/s4dIssueMessages';
import { getS4DProductHref, getS4DToppingHref } from '../utils/s4dAdminLinks';
import { S4DIssueList } from './S4DIssueList';
import { s4dStatusColor } from './S4DStatusIndicator';
import { getConfiguration } from '../../../shared/utils/configuration';
import { isLive } from '../../../shared/utils/productAndIngredientUtils';

type Props = {
  /** Every product, so that the counts do not change with the search and filters of the list. */
  products: ProductDto[];
  /** Every S4D product, for the ones that point at no product of ours. */
  s4dProducts: S4DProductDto[];
  comparisons: Map<number, S4DProductComparison>;
  /** Resolves an ingredient id to a name for the issue list. */
  getIngredientName: (ingredientId: number) => string | null | undefined;
  /** Descriptions of the data the comparison could not fetch, empty when all of it arrived. */
  failedSources: string[];
};

type ProductIssues = {
  productId: number;
  name: string;
  status: S4DProductStatus;
  isActive: boolean;
  blockingCount: number;
  warningCount: number;
  issues: S4DIssue[];
  /** Issues that have no S4D page to open, listed on their own above the groups. */
  ungroupedIssues: S4DIssue[];
  groups: IssueGroup[];
};

/** The issues of one S4D page of one S4D product, under a heading with a link to that page. */
type IssueGroup = {
  key: string;
  page: S4DPage;
  /** The S4D products to open, more than one only when the issues cannot be told apart. */
  s4dProducts: { s4dId: number; name: string }[];
  /** Set when the web code matches several S4D products, to tell the groups apart. */
  s4dProductName?: string;
  issues: S4DIssue[];
};

type ProductFilter = 'all' | 'active' | 'inactive';

// Products that cannot be sold as they are come first: they are the ones to fix.
const statusOrder: Record<S4DProductStatus, number> = {
  [S4DProductStatus.LinkBroken]: 0,
  [S4DProductStatus.DataDiffers]: 1,
  [S4DProductStatus.Ok]: 2,
  [S4DProductStatus.NotCompared]: 3,
};

/**
 * Sums up how well the products match S4D, and lists every difference behind one click so that the
 * products needing attention do not have to be found by scrolling the list.
 */
export const S4DIssuesSummary = (props: Props): JSX.Element => {
  const { products, s4dProducts, comparisons, getIngredientName, failedSources } = props;
  const { t } = useTranslation();
  const [isDialogOpen, setDialogOpen] = useState(false);
  const [expandedProductIds, setExpandedProductIds] = useState<number[]>([]);
  const [productFilter, setProductFilter] = useState<ProductFilter>('all');
  const [isCopied, setCopied] = useState(false);
  const s4dAdminBaseUrl = getConfiguration<string>('s4d.admin', '');

  const productIssues: ProductIssues[] = useMemo(() => {
    return products
      .map((product) => {
        const comparison = comparisons.get(product.productId || 0);
        if (!comparison) return undefined;

        const issues = getS4DIssues({ comparison, t, getIngredientName });
        if (issues.length === 0) return undefined;

        // One group per S4D page and product, so that a product with many issues reads as a couple
        // of short lists rather than a wall of repeated links.
        const groups: IssueGroup[] = [];

        issues
          .filter((issue) => issue.page)
          .forEach((issue) => {
            const key = `${issue.page}-${issue.s4dProductId ?? 'all'}`;
            const existing = groups.find((group) => group.key === key);

            if (existing) {
              existing.issues.push(issue);
              return;
            }

            // Inactive S4D products are not among the compared ones unless none is active, but their
            // issues link to them.
            const linkableS4DProducts = [
              ...comparison.s4dProducts.map((s4dProduct) => ({ s4dId: s4dProduct.s4dId, name: s4dProduct.name })),
              ...comparison.inactiveS4DProducts.map((s4dProduct) => ({
                s4dId: s4dProduct.s4dProductId,
                name: s4dProduct.name,
              })),
            ].filter((s4dProduct, index, all) => all.findIndex((other) => other.s4dId === s4dProduct.s4dId) === index);
            const groupS4DProducts = issue.s4dProductId
              ? linkableS4DProducts.filter((s4dProduct) => s4dProduct.s4dId === issue.s4dProductId)
              : comparison.s4dProducts.map((s4dProduct) => ({ s4dId: s4dProduct.s4dId, name: s4dProduct.name }));

            groups.push({
              key,
              page: issue.page as S4DPage,
              s4dProducts: groupS4DProducts,
              s4dProductName:
                comparison.s4dProducts.length > 1 && issue.s4dProductId ? groupS4DProducts[0]?.name : undefined,
              issues: [issue],
            });
          });

        return {
          productId: product.productId || 0,
          name: product.name,
          status: comparison.status,
          isActive: isLive(product),
          blockingCount: issues.filter(isBlockingS4DIssue).length,
          warningCount: issues.filter((issue) => !isBlockingS4DIssue(issue)).length,
          issues,
          ungroupedIssues: issues.filter((issue) => !issue.page),
          groups: groups.sort((a, b) => (a.page === b.page ? 0 : a.page === S4DPage.Prices ? -1 : 1)),
        };
      })
      .filter((row): row is ProductIssues => row !== undefined)
      .sort(
        (a, b) =>
          Number(b.isActive) - Number(a.isActive) ||
          statusOrder[a.status] - statusOrder[b.status] ||
          a.name.localeCompare(b.name)
      );
  }, [products, comparisons, getIngredientName, t]);

  // S4D products whose web code points at no product of ours. They belong to no product, so they
  // are listed on their own rather than under one.
  const orphanS4DProducts = useMemo(
    () =>
      getOrphanS4DProducts(
        s4dProducts,
        products.map((product) => product.productId || 0)
      ),
    [s4dProducts, products]
  );

  const activeProducts = productIssues.filter((row) => row.isActive);

  // An S4D product of its own is counted the way its kind is classified, like every other issue.
  const orphansBlock = isBlockingS4DIssueKind(S4DIssueKind.ProductOnlyInS4D);

  // The products the dialog lists, and the S4D products of their own, which are active ones.
  const visibleProducts =
    productFilter === 'all'
      ? productIssues
      : productIssues.filter((row) => row.isActive === (productFilter === 'active'));
  const visibleOrphans = productFilter === 'inactive' ? [] : orphanS4DProducts;

  const countIssues = (rows: ProductIssues[], orphans: number) => ({
    blocking: rows.reduce((count, row) => count + row.blockingCount, 0) + (orphansBlock ? orphans : 0),
    warnings: rows.reduce((count, row) => count + row.warningCount, 0) + (orphansBlock ? 0 : orphans),
  });

  const liveCounts = countIssues(activeProducts, orphanS4DProducts.length);
  const visibleCounts = countIssues(visibleProducts, visibleOrphans.length);

  const liveIssueCount = liveCounts.blocking + liveCounts.warnings;

  const toppingHref = (topping: S4DToppingRef): string => getS4DToppingHref(s4dAdminBaseUrl, topping);

  const s4dProductHref = (s4dId: number, page: S4DPage): string => getS4DProductHref(s4dAdminBaseUrl, s4dId, page);

  const countedText = (blocking: number, warnings: number): string => {
    const errors = t('s4dIssues.errors', { count: blocking });
    const differences = t('s4dIssues.differences', { count: warnings });

    if (blocking === 0) return differences;
    if (warnings === 0) return errors;

    return t('s4dIssues.errorsAndDifferences', { errors, differences });
  };

  const toggleProduct = (productId: number) =>
    setExpandedProductIds((expanded) =>
      expanded.includes(productId) ? expanded.filter((id) => id !== productId) : [...expanded, productId]
    );

  const isAllExpanded =
    visibleProducts.length > 0 && visibleProducts.every((row) => expandedProductIds.includes(row.productId));

  const toggleAll = () => setExpandedProductIds(isAllExpanded ? [] : visibleProducts.map((row) => row.productId));

  const copyAsText = async () => {
    const blocks = [
      ...(visibleOrphans.length > 0
        ? [
            [
              t('S4D products that match no product of ours'),
              ...visibleOrphans.map((s4dProduct) => `- ${s4dProduct.name} (${s4dProduct.webCode?.trim()})`),
            ].join('\n'),
          ]
        : []),
      ...visibleProducts.map((row) =>
        [
          `${row.name} (${row.productId})${row.isActive ? '' : ` - ${t('shared.activeLabel.inactive')}`}`,
          ...row.issues.map((issue) => `- ${getS4DIssueText(issue)}`),
        ].join('\n')
      ),
    ];

    try {
      await navigator.clipboard.writeText(blocks.join('\n\n'));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  const renderProduct = (row: ProductIssues) => {
    const isExpanded = expandedProductIds.includes(row.productId);

    return (
      <Box key={row.productId} sx={{ py: 0.5 }}>
        <Divider sx={{ mb: 0.5 }} />
        <Box display="flex" alignItems="center" gap={1}>
          <IconButton size="small" onClick={() => toggleProduct(row.productId)}>
            {isExpanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
          </IconButton>
          <Circle sx={{ fontSize: 10, color: s4dStatusColor[row.status] }} />
          <Link
            component={RouterLink}
            to={`/products/${row.productId}`}
            target="_blank"
            rel="noopener noreferrer"
            variant="subtitle1"
          >
            {row.name}
          </Link>
          <Typography variant="body2" color="text.secondary">
            {row.productId}
          </Typography>
          {!row.isActive && <Chip size="small" label={t('shared.activeLabel.inactive')} />}
          <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
            {countedText(row.blockingCount, row.warningCount)}
          </Typography>
        </Box>

        <Collapse in={isExpanded} unmountOnExit>
          <Box sx={{ pb: 1 }}>
            {row.ungroupedIssues.length > 0 && (
              <Box sx={{ pl: 4 }}>
                <S4DIssueList issues={row.ungroupedIssues} />
              </Box>
            )}

            {row.groups.map((group) => (
              <Box key={group.key} sx={{ mt: 1 }}>
                <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" sx={{ pl: 4 }}>
                  <Typography variant="subtitle2">
                    {group.page === S4DPage.Prices ? t('Pricing') : t('product.product')}
                    {group.s4dProductName ? `: ${group.s4dProductName}` : ''}
                  </Typography>
                  {group.s4dProducts.map((s4dProduct) => (
                    <Link
                      key={s4dProduct.s4dId}
                      variant="body2"
                      target="_blank"
                      rel="noopener noreferrer"
                      href={s4dProductHref(s4dProduct.s4dId, group.page)}
                    >
                      {t('S4D')}
                      {group.s4dProducts.length > 1 ? `: ${s4dProduct.name}` : ''}
                    </Link>
                  ))}
                </Box>
                <Box sx={{ pl: 4 }}>
                  <S4DIssueList issues={group.issues} toppingHref={toppingHref} />
                </Box>
              </Box>
            ))}
          </Box>
        </Collapse>
      </Box>
    );
  };

  if (failedSources.length > 0) {
    return (
      <SummaryRow>
        <ErrorOutline color="error" fontSize="small" />
        <Typography variant="body2" color="error">
          {t('S4D comparison failed. {{sources}}', { sources: failedSources.join(', ') })}
        </Typography>
      </SummaryRow>
    );
  }

  if (comparisons.size === 0) {
    return (
      <SummaryRow>
        <Circle color="disabled" fontSize="small" />
        <Typography variant="body2">{t('Fetching S4D data')}</Typography>
      </SummaryRow>
    );
  }

  return (
    <>
      <SummaryRow>
        {liveIssueCount === 0 ? (
          <CheckCircle color="success" fontSize="small" />
        ) : (
          <ErrorOutline
            fontSize="small"
            sx={{ color: liveCounts.blocking > 0 ? 'error.main' : s4dStatusColor[S4DProductStatus.DataDiffers] }}
          />
        )}
        <Link component="button" variant="body2" onClick={() => setDialogOpen(true)}>
          {t('S4D product data status')}
        </Link>
      </SummaryRow>

      <Dialog
        open={isDialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="md"
        fullWidth
        scroll="paper"
        PaperProps={{ sx: { maxHeight: 'min(80vh, 900px)' } }}
      >
        <DialogTitle display="flex" alignItems="center" gap={2} flexWrap="wrap">
          <Box>
            {t('S4D product data issues')}
            <Typography variant="body2" color="text.secondary">
              {countedText(visibleCounts.blocking, visibleCounts.warnings)}
            </Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={1} sx={{ ml: 'auto' }}>
            <TextField
              select
              size="small"
              value={productFilter}
              onChange={(event) => setProductFilter(event.target.value as ProductFilter)}
              sx={{ minWidth: 160 }}
            >
              <MenuItem value="all">{t('All')}</MenuItem>
              <MenuItem value="active">{t('Active products')}</MenuItem>
              <MenuItem value="inactive">{t('Inactive products')}</MenuItem>
            </TextField>
            <Button size="small" onClick={toggleAll} sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}>
              {isAllExpanded ? t('Collapse all') : t('Expand all')}
            </Button>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {visibleOrphans.length > 0 && (
            <Box sx={{ pb: 2 }}>
              <Typography variant="subtitle2">
                {t('S4D products that match no product of ours')} ({visibleOrphans.length})
              </Typography>
              <Box component="ul" sx={{ mt: 0.5, mb: 0, pl: 4 }}>
                {visibleOrphans.map((s4dProduct) => (
                  <Typography component="li" variant="body2" key={s4dProduct.s4dId}>
                    <Link
                      variant="body2"
                      target="_blank"
                      rel="noopener noreferrer"
                      href={s4dProductHref(s4dProduct.s4dId, S4DPage.Product)}
                    >
                      {s4dProduct.name}
                    </Link>
                    {` (${s4dProduct.webCode?.trim()})`}
                  </Typography>
                ))}
              </Box>
            </Box>
          )}

          {visibleProducts.map(renderProduct)}

          {visibleProducts.length === 0 && visibleOrphans.length === 0 && (
            <Box display="flex" alignItems="center" gap={1}>
              <CheckCircle color="success" fontSize="small" />
              <Typography variant="body2">{t('S4D product data is ok')}</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={copyAsText}>{isCopied ? t('Copied') : t('Copy as text')}</Button>
          <Button onClick={() => setDialogOpen(false)}>{t('global.close')}</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const SummaryRow = styled(Box)`
  display: flex;
  align-items: center;
  gap: 0.5em;
`;
